import { CoasterDocument, documentToFile, fileToDocument } from '../core/doc/document';
import { TrackRuntime } from '../core/track/runtime';
import { Centerline } from '../core/track/centerline';
import { autosmoothTrack } from '../core/track/smooth';
import { applyForcedOrientations } from '../core/track/orientation';
import { authoredRollKeysFromTrack } from '../core/track/flyBank';

export type Mode = 'build' | 'simulate';

export type StoreEvent =
  | 'track' // geometry/roll/sections changed → runtime was rebuilt
  | 'selection'
  | 'mode'
  | 'train'
  | 'doc' // whole document replaced (load/new/import/undo)
  | 'settings'
  | 'supports';

type Listener = (dirtyNodeIndices: number[] | null) => void;

/**
 * Document + selection + snapshot-based undo/redo. Every mutation goes through
 * commit()/gesture so undo state and derived-runtime rebuilds stay consistent.
 */
export class AppStore {
  doc: CoasterDocument;
  runtime: TrackRuntime;
  selection = new Set<string>();
  mode: Mode = 'build';
  /** Auto yaw/pitch at nodes that currently force orientation (for Track panel). */
  autoOrientationById = new Map<string, { yaw: number; pitch: number }>();

  private listeners = new Map<StoreEvent, Set<Listener>>();
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private gestureSnapshot: string | null = null;

  constructor(doc: CoasterDocument) {
    this.doc = doc;
    if (!this.doc.verticalInverts) this.doc.verticalInverts = [];
    this.prepareTrack();
    this.runtime = this.buildRuntime();
  }

  private buildRuntime(): TrackRuntime {
    // Banking is node-authored (gravity-relative); convert to the RMF channel.
    const cl = new Centerline(this.doc.track);
    this.doc.rollKeys = authoredRollKeysFromTrack(cl);
    return new TrackRuntime(this.doc.track, this.doc.rollKeys, this.doc.sections);
  }

  /** Autosmooth, then pin forced yaw/pitch handles and cache auto angles. */
  private prepareTrack(): void {
    autosmoothTrack(this.doc.track);
    this.autoOrientationById = applyForcedOrientations(this.doc.track);
  }

  on(event: StoreEvent, fn: Listener): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
  }

  emit(event: StoreEvent, dirty: number[] | null = null): void {
    for (const fn of this.listeners.get(event) ?? []) fn(dirty);
  }

  private serialize(): string {
    return JSON.stringify(documentToFile(this.doc));
  }

  /** One-shot undoable mutation. `dirtyNodes` limits mesh rebuilds when known. */
  commit(mutate: (doc: CoasterDocument) => void, dirtyNodes: number[] | null = null): void {
    this.pushUndo();
    mutate(this.doc);
    this.refresh(dirtyNodes);
  }

  /** Continuous gestures (drags): snapshot once at the start, push once at the end. */
  beginGesture(): void {
    if (this.gestureSnapshot === null) this.gestureSnapshot = this.serialize();
  }

  /** Called at pointer rate during a drag — no undo push, just derived-state refresh. */
  updateGesture(dirtyNodes: number[] | null = null): void {
    this.refresh(dirtyNodes);
  }

  endGesture(): void {
    if (this.gestureSnapshot !== null) {
      const now = this.serialize();
      if (now !== this.gestureSnapshot) {
        this.undoStack.push(this.gestureSnapshot);
        this.trimUndo();
        this.redoStack = [];
      }
      this.gestureSnapshot = null;
    }
  }

  private pushUndo(): void {
    this.undoStack.push(this.serialize());
    this.trimUndo();
    this.redoStack = [];
  }

  private trimUndo(): void {
    if (this.undoStack.length > 100) this.undoStack.shift();
  }

  undo(): void {
    const snap = this.undoStack.pop();
    if (!snap) return;
    this.redoStack.push(this.serialize());
    this.restore(snap);
  }

  redo(): void {
    const snap = this.redoStack.pop();
    if (!snap) return;
    this.undoStack.push(this.serialize());
    this.restore(snap);
  }

  private restore(snapshot: string): void {
    this.doc = fileToDocument(JSON.parse(snapshot));
    this.pruneSelection();
    this.prepareTrack();
    this.runtime = this.buildRuntime();
    this.emit('doc');
    this.emit('track');
    this.emit('selection');
  }

  /** Replace the whole document (load / import / new). */
  setDocument(doc: CoasterDocument): void {
    this.pushUndo();
    this.doc = doc;
    this.selection.clear();
    this.prepareTrack();
    this.runtime = this.buildRuntime();
    this.emit('doc');
    this.emit('track');
    this.emit('selection');
  }

  refresh(_dirtyNodes: number[] | null = null): void {
    this.prepareTrack();
    this.runtime = this.buildRuntime();
    // Autosmooth rewrites every handle, so local mesh dirty ranges are stale.
    this.emit('track', null);
  }

  setMode(mode: Mode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.emit('mode');
  }

  setSelection(ids: Iterable<string>): void {
    this.selection = new Set(ids);
    this.emit('selection');
  }

  toggleSelected(id: string): void {
    if (this.selection.has(id)) this.selection.delete(id);
    else this.selection.add(id);
    this.emit('selection');
  }

  private pruneSelection(): void {
    const valid = new Set(this.doc.track.nodes.map((n) => n.id));
    for (const id of [...this.selection]) if (!valid.has(id)) this.selection.delete(id);
  }

  selectedIndices(): number[] {
    const out: number[] = [];
    this.doc.track.nodes.forEach((n, i) => {
      if (this.selection.has(n.id)) out.push(i);
    });
    return out;
  }
}
